import Box from "@mui/material/Box";
import Collapse from "@mui/material/Collapse";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import Paper from "@mui/material/Paper";
import CloseIcon from "@mui/icons-material/Close";
import { useTranslationApp } from "../../lib/i18next";
import { BatteryMedium } from "lucide-react";
import { useState, useEffect } from "react";
import {
  NO_TASKS,
  NO_TASKS_DESCRIPTION,
  ACTIVITY,
} from "../../i18n/keysTranslations";
import CampaignAlertItem from "./CampaignAlertItem";
import GeneratingCreativeAlertItem from "./GeneratingCreativeAlertItem";
import GeneratingCustomReportAlertItem from "./GeneratingCustomReportAlertItem";
import AnalyzingImageLayersAlertItem from "./AnalyzingImageLayersAlertItem";
import AnalyzingCreativeTemplateAlertItem from "./AnalyzingCreativeTemplateAlertItem";
import ProducingCreativeTemplateAlertItem from "./ProducingCreativeTemplateAlertItem";
import ImageAnimationAlertItem from "./ImageAnimationAlertItem";
import {
  INPUT_COLOR_BORDER,
  NOTIFICATIONS_PANEL_BORDER,
} from "../../utils/colors";

/**
 * Notification item renderer based on type
 * @param {Object} notification - Notification object
 * @param {Function} onCloseAlert - Handler to close alert
 * @returns {JSX.Element|null} Rendered notification item
 */
const renderNotificationItem = (notification, onCloseAlert) => {
  switch (notification.type) {
    case "campaign":
      return (
        <Box key={notification.id}>
          <CampaignAlertItem alert={notification} onClose={onCloseAlert} />
        </Box>
      );
    case "generatingCreative":
      return (
        <Box key={notification.id}>
          <GeneratingCreativeAlertItem
            alert={notification}
            onClose={onCloseAlert}
          />
        </Box>
      );
    case "generatingCustomReport":
      return (
        <Box key={notification.id}>
          <GeneratingCustomReportAlertItem
            alert={notification}
            onClose={onCloseAlert}
          />
        </Box>
      );
    case "analyzingImageLayers":
      return (
        <Box key={notification.id}>
          <AnalyzingImageLayersAlertItem
            alert={notification}
            onClose={onCloseAlert}
          />
        </Box>
      );
    case "analyzingCreativeTemplate":
      return (
        <Box key={notification.id}>
          <AnalyzingCreativeTemplateAlertItem
            alert={notification}
            onClose={onCloseAlert}
          />
        </Box>
      );
    case "producingCreativeTemplate":
      return (
        <Box key={notification.id}>
          <ProducingCreativeTemplateAlertItem
            alert={notification}
            onClose={onCloseAlert}
          />
        </Box>
      );
    case "imageAnimation":
      return (
        <Box key={notification.id}>
          <ImageAnimationAlertItem
            alert={notification}
            onClose={onCloseAlert}
          />
        </Box>
      );
    default:
      console.warn(`Unknown notification type: ${notification.type}`);
      return null;
  }
};

/**
 * Notifications panel component
 * @param {Object} props
 * @param {boolean} props.isExpanded - Whether panel is expanded
 * @param {Array} props.activeAlerts - Array of active alerts
 * @param {Function} props.onClose - Handler to close panel
 * @param {Function} props.onCloseAlert - Handler to close individual alert
 * @param {number} props.zIndex - z-index value
 * @param {boolean} props.areModalsClosed - Whether both modals (ModalGallery and ModalCreateWithIA) are closed
 * @param {string|null} props.headerHint - Optional secondary caption under the Activity title
 */
export const NotificationsPanel = ({
  isExpanded,
  activeAlerts,
  onClose,
  onCloseAlert,
  zIndex = 1300,
  areModalsClosed = false,
  headerHint = null,
}) => {
  const { t } = useTranslationApp();
  const [isAdvertiserSelectorPresent, setIsAdvertiserSelectorPresent] =
    useState(false);

  useEffect(() => {
    const checkAdvertiserSelector = () => {
      const selectorElement = document.getElementById(
        "navbar-advertiser-selector"
      );
      const hasContent =
        selectorElement &&
        (selectorElement.children.length > 0 ||
          selectorElement.innerHTML.trim() !== "");
      setIsAdvertiserSelectorPresent(!!hasContent);
    };

    checkAdvertiserSelector();

    const observer = new MutationObserver(checkAdvertiserSelector);
    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    return () => {
      observer.disconnect();
    };
  }, []);

  const getRightValue = () => {
    if (areModalsClosed) {
      return isAdvertiserSelectorPresent ? 300 : 115;
    }

    return 24;
  };

  return (
    <Box
      sx={{
        position: "fixed",
        top: 80,
        right: { xs: 14, md: getRightValue() },
        zIndex: zIndex,
        pointerEvents: isExpanded ? "auto" : "none",
      }}
    >
      <Collapse
        in={isExpanded}
        timeout={200}
        easing={{
          enter: "cubic-bezier(0.4, 0, 0.2, 1)",
          exit: "cubic-bezier(0.4, 0, 0.2, 1)",
        }}
      >
        <Paper
          elevation={8}
          sx={{
            width: { xs: "calc(100vw - 32px)", md: 384 },
            maxWidth: { xs: "calc(100vw - 32px)", md: 384 },
            borderRadius: 3,
            border: `1px solid ${NOTIFICATIONS_PANEL_BORDER}`,
            bgcolor: "background.paper",
            overflow: "hidden",
            boxShadow:
              "0 20px 25px -5px rgba(0,0,0,0.1), 0 8px 10px -6px rgba(0,0,0,0.1)",
            transform: isExpanded ? "translateX(0)" : "translateX(16px)",
            opacity: isExpanded ? 1 : 0,
            transition: "transform 200ms ease-out, opacity 200ms ease-out",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: headerHint ? "flex-start" : "center",
              justifyContent: "space-between",
              gap: 1,
              ...(activeAlerts.length > 0 && {
                borderBottom: `1px solid ${INPUT_COLOR_BORDER}`,
              }),
              px: 2,
              py: 1.5,
            }}
          >
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography
                variant="subtitle2"
                sx={{
                  fontSize: "0.875rem",
                  fontWeight: 500,
                  color: "text.primary",
                }}
              >
                {activeAlerts.length > 0 ? t(ACTIVITY) : ""}
              </Typography>
              {headerHint ? (
                <Typography
                  variant="caption"
                  sx={{
                    display: "block",
                    mt: 0.25,
                    fontSize: "0.75rem",
                    color: "text.secondary",
                  }}
                >
                  {headerHint}
                </Typography>
              ) : null}
            </Box>
            <IconButton
              onClick={onClose}
              size="small"
              aria-label="close"
              sx={{
                color: "text.secondary",
                borderRadius: 1.5,
                p: 0.5,
                flexShrink: 0,
                "&:hover": {
                  bgcolor: "action.hover",
                  color: "text.primary",
                },
              }}
            >
              <CloseIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Box>

          <Box
            sx={{
              maxHeight: 320,
              overflowY: "auto",
              p: 1.5,
              display: "flex",
              flexDirection: "column",
              gap: 1,
            }}
          >
            {activeAlerts.length === 0 ? (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  pb: 3,
                  textAlign: "center",
                }}
              >
                <BatteryMedium size={48} strokeWidth={1.5} />
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: "0.875rem",
                    fontWeight: 500,
                    mt: 1.5,
                    color: "text.primary",
                  }}
                >
                  {t(NO_TASKS)}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    fontSize: "0.75rem",
                    mt: 0.5,
                    color: "text.secondary",
                  }}
                >
                  {t(NO_TASKS_DESCRIPTION)}
                </Typography>
              </Box>
            ) : (
              activeAlerts.map((notification) =>
                renderNotificationItem(notification, onCloseAlert)
              )
            )}
          </Box>
        </Paper>
      </Collapse>
    </Box>
  );
};

export default NotificationsPanel;
